import { Link } from '@tanstack/react-router'
import { CalendarDays, Clock3 } from 'lucide-react'
import { RESOURCE_COPY } from '../content/resource-copy'
import {
  RESOURCE_TYPE_CONFIG,
  getRelatedResources,
  getResourcesIndexPath,
} from '../content/resources'
import { ResourceCard } from './resource-card'
import { ResourceCta } from './resource-cta'
import { ResourceTelegramCta } from './resource-telegram-cta'
import type { Resource, ResourceContentBlock } from '../content/resources'
import { GtmPageShell } from '@/features/gtm/components/gtm-page-shell'

type ResourceArticlePageProps = {
  resource: Resource
}

function formatPublishedDate(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))
}

function ResourceTableOfContents({ resource }: ResourceArticlePageProps) {
  const copy = RESOURCE_COPY[resource.language]
  return (
    <nav aria-label={copy.tableOfContents}>
      <p className="text-[11px] font-medium uppercase tracking-[0.17em] text-muted-foreground">
        {copy.onThisPage}
      </p>
      <ol className="mt-4 space-y-2.5 text-sm leading-5">
        {resource.sections.map((section) => (
          <li key={section.id} className={section.level === 3 ? 'pl-4' : ''}>
            <a
              href={`#${section.id}`}
              className="text-muted-foreground transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {section.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

function ResourceBlock({ block }: { block: ResourceContentBlock }) {
  if (block.type === 'paragraph') {
    return (
      <p className="text-base leading-8 text-foreground/80">{block.text}</p>
    )
  }

  if (block.type === 'list') {
    return (
      <ul className="space-y-3 pl-5 text-base leading-7 text-foreground/80 marker:text-upgrade-cta">
        {block.items.map((item) => (
          <li key={item} className="pl-1">
            {item}
          </li>
        ))}
      </ul>
    )
  }

  if (block.type === 'callout') {
    return (
      <aside className="rounded-[1.15rem] border border-upgrade-cta/25 bg-upgrade-cta/8 p-5 sm:p-6">
        <p className="font-semibold text-foreground">{block.title}</p>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {block.body}
        </p>
      </aside>
    )
  }

  if (block.type === 'source') {
    return (
      <aside className="rounded-[1.15rem] border border-border bg-card/85 p-5 sm:p-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.17em] text-muted-foreground">
          {block.label}
        </p>
        {block.description ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {block.description}
          </p>
        ) : null}
        <a
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex text-sm font-medium text-upgrade-cta underline decoration-upgrade-cta/45 underline-offset-4 transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {block.href}
        </a>
      </aside>
    )
  }

  if (block.type === 'metrics') {
    return (
      <figure>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {block.items.map((item) => (
            <div
              key={`${item.value}-${item.label}`}
              className="rounded-[1.15rem] border border-upgrade-cta/20 bg-gradient-to-br from-upgrade-cta/14 to-card/35 p-4 sm:p-6"
            >
              <p className="text-3xl font-semibold tracking-[-0.04em] text-upgrade-cta sm:text-4xl">
                {item.value}
              </p>
              <p className="mt-2 text-sm font-medium leading-5 text-foreground sm:text-base">
                {item.label}
              </p>
              {item.detail ? (
                <p className="mt-1 text-xs leading-5 text-muted-foreground sm:text-sm">
                  {item.detail}
                </p>
              ) : null}
            </div>
          ))}
        </div>
        {block.sourceNote ? (
          <figcaption className="mt-3 text-center text-xs leading-5 text-muted-foreground">
            {block.sourceNote}
          </figcaption>
        ) : null}
      </figure>
    )
  }

  if (block.type === 'table') {
    return (
      <div className="overflow-x-auto rounded-[1.15rem] border border-border">
        <table className="w-full min-w-[34rem] border-collapse text-left">
          <thead>
            <tr className="border-b border-border bg-card/85">
              {block.columns.map((column) => (
                <th
                  key={column}
                  className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr
                key={row[0]}
                className="border-b border-border/60 last:border-0"
              >
                <th
                  scope="row"
                  className="w-1/3 px-5 py-4 align-top text-sm font-semibold text-foreground"
                >
                  {row[0]}
                </th>
                <td className="px-5 py-4 align-top text-sm leading-6 text-muted-foreground">
                  {row[1]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (block.type === 'faq') {
    return (
      <dl className="space-y-4">
        {block.items.map((item) => (
          <div
            key={item.question}
            className="rounded-[1.15rem] border border-border bg-card/85 p-5 sm:p-6"
          >
            <dt className="font-semibold leading-6 text-foreground">
              {item.question}
            </dt>
            <dd className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  return (
    <figure className="overflow-hidden rounded-[1.35rem] border border-border bg-muted p-2 sm:p-3">
      <img
        src={block.src}
        alt={block.alt}
        loading="lazy"
        decoding="async"
        className="mx-auto max-h-[36rem] w-auto rounded-[0.85rem]"
      />
      {block.caption ? (
        <figcaption className="px-3 pb-2 pt-4 text-center text-sm leading-6 text-muted-foreground">
          {block.caption}
        </figcaption>
      ) : null}
    </figure>
  )
}

export function ResourceArticlePage({ resource }: ResourceArticlePageProps) {
  const copy = RESOURCE_COPY[resource.language]
  const resourcesIndexPath = getResourcesIndexPath(resource.language)
  const typeConfig = RESOURCE_TYPE_CONFIG[resource.language][resource.type]
  const relatedResources = getRelatedResources(resource)

  return (
    <GtmPageShell quiet hideTelegram>
      <article className="pb-8 pt-12 sm:pt-16">
        <nav
          aria-label={copy.breadcrumbLabel}
          className="text-sm text-muted-foreground"
        >
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link
                to={resourcesIndexPath}
                className="transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.resources}
              </Link>
            </li>
            <li aria-hidden="true">→</li>
            <li>
              <a
                href={`${resourcesIndexPath}#${typeConfig.segment}`}
                className="transition-colors hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {typeConfig.pluralLabel}
              </a>
            </li>
            <li aria-hidden="true">→</li>
            <li className="text-foreground/80" aria-current="page">
              {resource.title}
            </li>
          </ol>
        </nav>

        <header className="mx-auto max-w-4xl pb-10 pt-14 text-center sm:pb-12">
          <span className="text-[11px] uppercase tracking-[0.18em] text-upgrade-cta">
            {typeConfig.label}
          </span>
          <h1 className="mt-5 text-4xl font-display font-medium leading-[1.04] tracking-[-0.015em] text-foreground sm:text-5xl lg:text-6xl">
            {resource.title}
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-muted-foreground sm:text-xl">
            {resource.description}
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="font-medium text-foreground/80">
              {resource.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="size-4" aria-hidden="true" />
              <time dateTime={resource.publishedAt}>
                {formatPublishedDate(resource.publishedAt, copy.locale)}
              </time>
            </span>
            {resource.updatedAt ? (
              <span>
                {copy.updated}{' '}
                <time dateTime={resource.updatedAt}>
                  {formatPublishedDate(resource.updatedAt, copy.locale)}
                </time>
              </span>
            ) : null}
            {resource.readingTime ? (
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="size-4" aria-hidden="true" />
                {resource.readingTime}
              </span>
            ) : null}
          </div>
        </header>

        {resource.coverImage ? (
          <figure className="overflow-hidden rounded-[1.6rem] border border-border bg-muted p-2 shadow-lg sm:p-3">
            <img
              src={resource.coverImage}
              alt={resource.coverImageAlt ?? ''}
              width={1440}
              height={900}
              decoding="async"
              className="block h-auto w-full rounded-[1.05rem]"
            />
          </figure>
        ) : null}

        <div className="mx-auto mt-12 max-w-5xl lg:grid lg:grid-cols-[minmax(0,44rem)_15rem] lg:items-start lg:gap-16">
          <div className="min-w-0">
            <section
              aria-labelledby="key-takeaways-heading"
              className="rounded-[1.35rem] border border-border bg-card/85 p-6 sm:p-8"
            >
              <h2
                id="key-takeaways-heading"
                className="text-xl font-semibold tracking-[-0.02em] text-foreground"
              >
                {copy.keyTakeaways}
              </h2>
              <ul className="mt-5 space-y-3 pl-5 text-[15px] leading-7 text-muted-foreground marker:text-upgrade-cta">
                {resource.keyTakeaways.map((takeaway) => (
                  <li key={takeaway} className="pl-1">
                    {takeaway}
                  </li>
                ))}
              </ul>
            </section>

            <details className="mt-6 rounded-[1.15rem] border border-border bg-card/85 p-5 lg:hidden">
              <summary className="cursor-pointer font-medium text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {copy.tableOfContents}
              </summary>
              <div className="mt-5 border-t border-border pt-5">
                <ResourceTableOfContents resource={resource} />
              </div>
            </details>

            <div className="mt-12 space-y-10">
              {resource.sections.map((section, index) => {
                const Heading = section.level === 2 ? 'h2' : 'h3'
                return (
                  <div key={section.id}>
                    <section
                      aria-labelledby={section.id}
                      className="scroll-mt-8 space-y-5"
                    >
                      <Heading
                        id={section.id}
                        className={
                          section.level === 2
                            ? 'pt-2 text-3xl font-display font-medium tracking-[-0.01em] text-foreground'
                            : 'pt-1 text-2xl font-semibold tracking-[-0.025em] text-foreground'
                        }
                      >
                        {section.title}
                      </Heading>
                      {section.blocks.map((block, blockIndex) => (
                        <ResourceBlock
                          key={`${section.id}-${block.type}-${blockIndex}`}
                          block={block}
                        />
                      ))}
                    </section>
                    {index === 1 ? (
                      <ResourceCta
                        type={resource.ctaType ?? 'build-agent'}
                        placement="article"
                        language={resource.language}
                      />
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>

          <aside className="sticky top-8 hidden border-l border-border pl-7 lg:block">
            <ResourceTableOfContents resource={resource} />
          </aside>
        </div>
      </article>

      {relatedResources.length > 0 ? (
        <section
          aria-labelledby="related-resources-heading"
          className="border-t border-border py-16"
        >
          <h2
            id="related-resources-heading"
            className="text-2xl font-display font-medium tracking-[-0.01em] text-foreground sm:text-3xl"
          >
            {copy.relatedResources}
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {relatedResources.map((relatedResource) => (
              <ResourceCard
                key={relatedResource.slug}
                resource={relatedResource}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* The Telegram channel is in Russian. */}

      {resource.language === 'ru' ? (
        <ResourceTelegramCta language={resource.language} placement="article" />
      ) : null}

      <ResourceCta
        type="book-demo"
        placement="final"
        language={resource.language}
      />
    </GtmPageShell>
  )
}
